import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Camera,
    Briefcase,
    Users,
    Smartphone,
    Tag,
    Search,
    Settings,
    Home,
    Mail,
    KeyRound,
    Megaphone,
    Monitor as MonitorIcon,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
} from '@/src/shadComponents/ui/dialog';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { FeatureFlags } from '@/src/Features/lib/constants';

interface SearchResult {
    id: number;
    name: string;
    type: 'activation' | 'job' | 'account' | 'device' | 'tag';
    description: string | null;
}

interface QuickLink {
    name: string;
    path: string;
    icon: any;
    flag?: string;
}

const quickLinks: QuickLink[] = [
    { name: 'Dashboard', path: '/home', icon: Home },
    { name: 'Command Center', path: '/command-center', icon: MonitorIcon },
    { name: 'Activations', path: '/activations', icon: Camera, flag: FeatureFlags.ACTIVATIONS_VIEWER_V1 },
    { name: 'Jobs', path: '/jobs', icon: Briefcase, flag: FeatureFlags.JOBS_VIEWER_V1 },
    { name: 'Accounts', path: '/accounts', icon: Users, flag: FeatureFlags.ACCOUNT_ADMIN_V1 },
    { name: 'Devices', path: '/device-management', icon: Smartphone, flag: FeatureFlags.DEVICE_VIEWER_V1 },
    { name: 'Email Templates', path: '/templates', icon: Mail },
    { name: 'Whitelabel Settings', path: '/org/whitelabel-settings', icon: Tag, flag: FeatureFlags.WHITELABEL_SETTINGS_V1 },
    { name: 'Organization Settings', path: '/org', icon: Settings },
    { name: 'Licenses', path: '/admin/licenses', icon: KeyRound, flag: FeatureFlags.GLOBAL_ADMIN_V1 },
    { name: 'Announcements', path: '/admin/announcements', icon: Megaphone, flag: FeatureFlags.GLOBAL_ADMIN_V1 },
];

const typeIcons: Record<string, any> = {
    activation: Camera,
    job: Briefcase,
    account: Users,
    device: Smartphone,
    tag: Tag,
};

const typeLabels: Record<string, string> = {
    activation: 'Activation',
    job: 'Job',
    account: 'Account',
    device: 'Device',
    tag: 'Tag',
};

function getResultPath(result: SearchResult): string {
    switch (result.type) {
        case 'activation':
            return `/activations/${result.id}`;
        case 'job':
            return `/jobs/${result.id}`;
        case 'account':
            return `/accounts/${result.id}`;
        case 'device':
            return `/device-management`;
        case 'tag':
            return `/activations`;
        default:
            return '/';
    }
}

export default function OmniSearch() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const featureFlags = useContext(FeatureFlagContext);

    const filteredQuickLinks = quickLinks.filter(
        (link) => !link.flag || featureFlags[link.flag],
    );

    // Keyboard shortcut to open
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const doSearch = useCallback(
        async (q: string) => {
            if (!q.trim()) {
                setResults([]);
                setIsSearching(false);
                return;
            }
            setIsSearching(true);
            try {
                const response = await authenticatedApi.get(
                    `/${orgId}/search?q=${encodeURIComponent(q.trim())}`,
                );
                setResults(response.data);
            } catch {
                setResults([]);
            } finally {
                setIsSearching(false);
            }
        },
        [authenticatedApi, orgId],
    );

    const handleInputChange = (value: string) => {
        setQuery(value);
        setSelectedIndex(0);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(value), 250);
    };

    const handleOpenChange = (v: boolean) => {
        setOpen(v);
        if (!v) {
            setQuery('');
            setResults([]);
            setSelectedIndex(0);
        }
    };

    const navigateAndClose = (path: string) => {
        navigate(path);
        handleOpenChange(false);
    };

    // Build combined list for keyboard nav
    const showQuickLinks = !query.trim();
    const items: { label: string; description?: string | null; icon: any; path: string; section?: string }[] = [];

    if (showQuickLinks) {
        for (const link of filteredQuickLinks) {
            items.push({
                label: link.name,
                icon: link.icon,
                path: link.path,
                section: 'Go to',
            });
        }
    } else {
        for (const result of results) {
            items.push({
                label: result.name,
                description: result.description,
                icon: typeIcons[result.type] || Search,
                path: getResultPath(result),
                section: typeLabels[result.type],
            });
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((i) => Math.min(i + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && items[selectedIndex]) {
            e.preventDefault();
            navigateAndClose(items[selectedIndex].path);
        }
    };

    // Group items by section
    const grouped: { section: string; items: typeof items }[] = [];
    for (const item of items) {
        const section = item.section || '';
        const existing = grouped.find((g) => g.section === section);
        if (existing) {
            existing.items.push(item);
        } else {
            grouped.push({ section, items: [item] });
        }
    }

    let flatIndex = 0;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="flex w-full max-w-md items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
            >
                <Search className="size-3.5 shrink-0" />
                <span className="hidden sm:inline flex-1 text-left">Search...</span>
                <kbd className="hidden sm:inline-flex h-5 shrink-0 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground ml-auto">
                    <span className="text-xs">&#8984;</span>K
                </kbd>
            </button>

            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden [&>button:last-child]:hidden">
                    <div className="flex items-center gap-2 border-b px-4">
                        <Search className="size-4 shrink-0 text-muted-foreground" />
                        <input
                            ref={inputRef}
                            placeholder="Search activations, jobs, accounts, devices..."
                            value={query}
                            onChange={(e) => handleInputChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                            autoFocus
                        />
                    </div>

                    <div className="max-h-[360px] overflow-y-auto">
                        {isSearching && (
                            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                Searching...
                            </div>
                        )}

                        {!isSearching && query.trim() && results.length === 0 && (
                            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                No results for &ldquo;{query}&rdquo;
                            </div>
                        )}

                        {!isSearching && items.length > 0 && (
                            <div className="py-2">
                                {grouped.map((group) => (
                                    <div key={group.section}>
                                        <div className="px-4 py-1.5 text-xs font-medium text-muted-foreground">
                                            {group.section}
                                        </div>
                                        {group.items.map((item) => {
                                            const idx = flatIndex++;
                                            const Icon = item.icon;
                                            return (
                                                <button
                                                    key={`${item.path}-${idx}`}
                                                    onClick={() =>
                                                        navigateAndClose(
                                                            item.path,
                                                        )
                                                    }
                                                    onMouseEnter={() =>
                                                        setSelectedIndex(idx)
                                                    }
                                                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm cursor-pointer ${
                                                        idx === selectedIndex
                                                            ? 'bg-accent text-accent-foreground'
                                                            : 'text-foreground'
                                                    }`}
                                                >
                                                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                                                    <div className="flex-1 min-w-0">
                                                        <span className="truncate">
                                                            {item.label}
                                                        </span>
                                                        {item.description && (
                                                            <span className="ml-2 text-xs text-muted-foreground">
                                                                {
                                                                    item.description
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
